(function (window) {
  'use strict';

  var snapshotCache = null;
  var freshSnapshot = null;
  var seededFallback = null;
  var snapshotRequests = Object.create(null);
  var snapshotRequestId = 0;
  var snapshotCacheLifetime = 30000;
  var freshSnapshotLifetime = 300;
  var seededFallbackLifetime = 1000;

  function amount(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function finalLine(item) {
    return amount(item && item.final_line_price, amount(item && item.line_price, 0));
  }

  function line(item, compareAtPrice) {
    var quantity = Math.max(0, amount(item && item.quantity, 0));
    var original = amount(item && item.original_line_price, 0);
    var compareAt = amount(compareAtPrice, 0);
    var retail = Math.max(original, compareAt > 0 ? compareAt * quantity : 0);
    var final = finalLine(item);
    var savings = Math.max(0, retail - final);
    return {
      retail: retail,
      final: final,
      savings: savings,
      percent: retail > final ? Math.round((savings * 100) / retail) : 0
    };
  }

  function unit(item, compareAtPrice) {
    var original = amount(item && item.original_price, 0);
    var compareAt = amount(compareAtPrice, 0);
    var retail = Math.max(original, compareAt > 0 ? compareAt : 0);
    var final = amount(item && item.final_price, amount(item && item.price, 0));
    var savings = Math.max(0, retail - final);
    return {
      retail: retail,
      final: final,
      savings: savings,
      percent: retail > final ? Math.round((savings * 100) / retail) : 0
    };
  }

  function isLiquidation(item, compareAtPrice, options) {
    if (!options || options.enabled !== true || typeof options.threshold !== 'number'
      || !Number.isFinite(options.threshold) || options.threshold < 0) return false;
    var currentPrice = item && item.catalog_price;
    if (typeof currentPrice !== 'number' || !Number.isSafeInteger(currentPrice) || currentPrice < 0
      || typeof compareAtPrice !== 'number' || !Number.isSafeInteger(compareAtPrice) || compareAtPrice <= currentPrice) return false;
    return (compareAtPrice - currentPrice) * 100 > compareAtPrice * options.threshold;
  }

  function liquidationOptions(element) {
    var threshold = Number(element && element.getAttribute('data-merlons-cart-pricing-liquidation-threshold'));
    return {
      enabled: element && element.getAttribute('data-merlons-cart-pricing-liquidation-enabled') === 'true',
      threshold: threshold
    };
  }

  function currencyCode(value) {
    var currency = value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'currency')
      ? value.currency
      : value;
    if (currency && typeof currency === 'object') currency = currency.iso_code || currency.code || '';
    return String(currency || '').trim().toUpperCase();
  }

  function identifier(value) {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  function variantId(item) {
    return identifier(item && (item.variant_id === undefined ? item.id : item.variant_id));
  }

  function tokenParts(value) {
    var raw = String(value || '').trim();
    if (!raw) return null;
    var queryAt = raw.indexOf('?');
    if (queryAt === -1) return { raw: raw, base: raw, key: '', wrapper: false, valid: true };
    var base = raw.slice(0, queryAt);
    var query = raw.slice(queryAt + 1);
    var invalid = { raw: raw, base: base, key: '', wrapper: true, valid: false };
    if (!base || query.indexOf('&') !== -1 || query.indexOf('key=') !== 0) return invalid;
    var encodedKey = query.slice(4);
    if (!encodedKey || encodedKey.indexOf('?') !== -1) return invalid;
    try {
      var key = decodeURIComponent(encodedKey.replace(/\+/g, ' '));
      if (!key) return invalid;
      return { raw: raw, base: base, key: key, wrapper: true, valid: true };
    } catch (error) {
      return invalid;
    }
  }

  // Shopify advises themes to treat cart tokens as opaque. Exact opaque tokens
  // always match. The only cross-format compatibility is the observed bare /
  // single nonempty `?key=` wrapper pair with the same opaque base; no token
  // length, prefix, unknown parameter, or alternate query shape is inferred.
  function tokensMatch(left, right) {
    var leftToken = tokenParts(left);
    var rightToken = tokenParts(right);
    if (!leftToken || !rightToken) return false;
    if (leftToken.raw === rightToken.raw) return true;
    if (!leftToken.valid || !rightToken.valid || leftToken.base !== rightToken.base) return false;
    return leftToken.wrapper !== rightToken.wrapper;
  }

  function currentQuantity(item) {
    return amount(item && item.quantity, -1);
  }

  function itemMap(items) {
    var byKey = Object.create(null);
    var valid = true;
    (items || []).forEach(function (item) {
      var key = identifier(item && item.key);
      if (!key || Object.prototype.hasOwnProperty.call(byKey, key)) {
        valid = false;
        return;
      }
      byKey[key] = item;
    });
    return valid ? byKey : null;
  }

  function hasCompareAtPrice(item) {
    return Boolean(item)
      && Object.prototype.hasOwnProperty.call(item, 'compare_at_price')
      && typeof item.compare_at_price === 'number'
      && Number.isFinite(item.compare_at_price)
      && Number.isInteger(item.compare_at_price)
      && item.compare_at_price >= 0;
  }

  function snapshotRelation(cart, snapshot, allowUnverifiedToken) {
    if (!cart || !snapshot || !Array.isArray(cart.items) || !Array.isArray(snapshot.items)) return { matches: false, cacheable: false };
    if (snapshot.merlons_cart_pricing !== true) return { matches: false, cacheable: false };
    if (!currencyCode(cart) || currencyCode(snapshot) !== currencyCode(cart)) return { matches: false, cacheable: false };
    if (snapshot.items.length !== cart.items.length) return { matches: false, cacheable: false };
    var currentByKey = itemMap(cart.items);
    var snapshotByKey = itemMap(snapshot.items);
    if (!currentByKey || !snapshotByKey) return { matches: false, cacheable: false };
    var currentKeys = Object.keys(currentByKey);
    if (currentKeys.length !== Object.keys(snapshotByKey).length) return { matches: false, cacheable: false };
    var matchingLines = currentKeys.every(function (key) {
      var item = currentByKey[key];
      var entry = snapshotByKey[key];
      return entry
        && hasCompareAtPrice(entry)
        && currentQuantity(entry) === currentQuantity(item)
        && variantId(entry) === variantId(item);
    });
    if (!matchingLines) return { matches: false, cacheable: false };

    var cartToken = tokenParts(cart.token);
    var snapshotToken = tokenParts(snapshot.token);
    if (!cartToken || !snapshotToken) return allowUnverifiedToken
      ? { matches: true, cacheable: false }
      : { matches: false, cacheable: false };
    if (tokensMatch(cart.token, snapshot.token)) return { matches: true, cacheable: true };
    return allowUnverifiedToken ? { matches: true, cacheable: false } : { matches: false, cacheable: false };
  }

  function snapshotMatches(cart, snapshot) {
    return snapshotRelation(cart, snapshot).matches;
  }

  function comparisons(cart, snapshot) {
    var map = Object.create(null);
    if (!snapshotRelation(cart, snapshot).matches) return map;
    return comparisonMap(cart, snapshot);
  }

  function comparisonMap(cart, snapshot) {
    var map = Object.create(null);
    var catalogByKey = Object.create(null);
    var currentByKey = itemMap(cart && cart.items);
    if (!currentByKey) return map;
    (snapshot && snapshot.items || []).forEach(function (item) {
      var key = identifier(item && item.key);
      if (Object.prototype.hasOwnProperty.call(currentByKey, key)) {
        map[key] = Math.max(0, amount(item.compare_at_price, 0));
        if (typeof item.variant_price === 'number' && Number.isSafeInteger(item.variant_price) && item.variant_price >= 0) {
          catalogByKey[key] = item.variant_price;
        }
      }
    });
    return markCatalogPrices(map, catalogByKey);
  }

  function comparisonTrust(map) {
    return map && map.__merlonsCartPricingTrust || '';
  }

  function catalogPrices(map) {
    return map && map.__merlonsCartCatalogPrices || Object.create(null);
  }

  function catalogPrice(map, key, fallback) {
    var prices = catalogPrices(map);
    var value = Object.prototype.hasOwnProperty.call(prices, key) ? prices[key] : fallback;
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function markComparisons(map, trust) {
    try {
      Object.defineProperty(map, '__merlonsCartPricingTrust', { value: trust, enumerable: false, configurable: true, writable: true });
    } catch (error) {
      try { map.__merlonsCartPricingTrust = trust; } catch (ignored) { }
    }
    return map;
  }

  function markCatalogPrices(map, prices) {
    try {
      Object.defineProperty(map, '__merlonsCartCatalogPrices', { value: prices, enumerable: false, configurable: true, writable: true });
    } catch (error) {
      try { map.__merlonsCartCatalogPrices = prices; } catch (ignored) { }
    }
    return map;
  }

  function cacheComparisons(cart, compareAtByKey, snapshot, source) {
    var relation = snapshotRelation(cart, snapshot);
    if (!relation.cacheable) return;
    var catalogByKey = catalogPrices(compareAtByKey);
    if (source === 'seed' && snapshotCache && snapshotCache.source === 'snapshot'
      && snapshotCache.currency === currencyCode(cart)
      && tokensMatch(snapshotCache.token && snapshotCache.token.raw, cart.token)
      && Date.now() <= snapshotCache.expiresAt) {
      (cart.items || []).forEach(function (item) {
        var key = identifier(item && item.key);
        if (key && !Object.prototype.hasOwnProperty.call(snapshotCache.entries, key)
          && Object.prototype.hasOwnProperty.call(compareAtByKey, key)
          && Object.prototype.hasOwnProperty.call(catalogByKey, key)) {
          snapshotCache.entries[key] = {
            variantId: variantId(item),
            compareAt: Math.max(0, amount(compareAtByKey[key], 0)),
            catalogPrice: catalogByKey[key]
          };
        }
      });
      return;
    }
    snapshotCache = {
      token: tokenParts(cart.token),
      currency: currencyCode(cart),
      source: source || 'snapshot',
      entries: (cart.items || []).reduce(function (entries, item) {
        var key = identifier(item && item.key);
        if (key && Object.prototype.hasOwnProperty.call(compareAtByKey, key)
          && Object.prototype.hasOwnProperty.call(catalogByKey, key)) {
          entries[key] = {
            variantId: variantId(item),
            compareAt: Math.max(0, amount(compareAtByKey[key], 0)),
            catalogPrice: catalogByKey[key]
          };
        }
        return entries;
      }, Object.create(null)),
      expiresAt: Date.now() + snapshotCacheLifetime
    };
  }

  function cachedComparisons(cart) {
    var map = Object.create(null);
    var catalogByKey = Object.create(null);
    if (!snapshotCache || !cart || Date.now() > snapshotCache.expiresAt) {
      if (snapshotCache && Date.now() > snapshotCache.expiresAt) snapshotCache = null;
      return map;
    }
    if (snapshotCache.currency !== currencyCode(cart) || !tokensMatch(snapshotCache.token && snapshotCache.token.raw, cart.token)) return map;
    (cart.items || []).forEach(function (item) {
      var key = identifier(item && item.key);
      var entry = snapshotCache.entries[key];
      if (entry && entry.variantId === variantId(item)) {
        map[key] = entry.compareAt;
        if (typeof entry.catalogPrice === 'number' && Number.isSafeInteger(entry.catalogPrice) && entry.catalogPrice >= 0) {
          catalogByKey[key] = entry.catalogPrice;
        }
      }
    });
    return markCatalogPrices(markComparisons(map, 'strict'), catalogByKey);
  }

  function copyComparisons(source) {
    var copy = Object.create(null);
    var copiedCatalogPrices = Object.create(null);
    Object.keys(source || {}).forEach(function (key) { copy[key] = source[key]; });
    Object.keys(catalogPrices(source)).forEach(function (key) { copiedCatalogPrices[key] = catalogPrices(source)[key]; });
    return markCatalogPrices(markComparisons(copy, comparisonTrust(source)), copiedCatalogPrices);
  }

  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    var lines = cart.items.map(function (item) {
      return [identifier(item && item.key), variantId(item), currentQuantity(item)];
    }).sort(function (left, right) {
      return JSON.stringify(left).localeCompare(JSON.stringify(right));
    });
    return JSON.stringify([currencyCode(cart), tokenParts(cart.token) && tokenParts(cart.token).raw, lines]);
  }

  function rememberFreshSnapshot(cart, compareAtByKey, snapshot) {
    if (!snapshotRelation(cart, snapshot).cacheable) return;
    freshSnapshot = {
      signature: cartSignature(cart),
      map: copyComparisons(compareAtByKey),
      expiresAt: Date.now() + freshSnapshotLifetime
    };
  }

  function seededComparisons(cart) {
    var signature = cartSignature(cart);
    if (!seededFallback || !signature || seededFallback.signature !== signature || Date.now() > seededFallback.expiresAt) {
      if (seededFallback && Date.now() > seededFallback.expiresAt) seededFallback = null;
      return Object.create(null);
    }
    return markComparisons(copyComparisons(seededFallback.map), 'seed');
  }

  function fallbackComparisons(cart) {
    var map = cachedComparisons(cart);
    var seed = seededComparisons(cart);
    var catalogByKey = catalogPrices(map);
    var seedCatalogByKey = catalogPrices(seed);
    var mergedSeed = false;
    Object.keys(seed).forEach(function (key) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) {
        map[key] = seed[key];
        if (Object.prototype.hasOwnProperty.call(seedCatalogByKey, key)) catalogByKey[key] = seedCatalogByKey[key];
        mergedSeed = true;
      }
    });
    return markCatalogPrices(
      markComparisons(map, mergedSeed ? 'unverified' : (comparisonTrust(map) || comparisonTrust(seed))),
      catalogByKey
    );
  }

  function seedFromDom(cart, root) {
    var map = Object.create(null);
    var catalogByKey = Object.create(null);
    var scope = root && typeof root.querySelectorAll === 'function' ? root : null;
    if (!scope || !cart || !Array.isArray(cart.items)) return map;
    var currentCurrency = currencyCode(cart);
    var currentToken = cart.token;
    var expectedItems = itemMap(cart.items);
    if (!expectedItems) return map;
    var sources = Object.create(null);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-merlons-cart-pricing-line]'), function (element) {
      if (element.getAttribute('data-merlons-cart-pricing-unit') === 'true') return;
      var sourceCurrency = currencyCode(element.getAttribute('data-merlons-cart-pricing-currency'));
      var key = identifier(element.getAttribute('data-merlons-cart-pricing-key'));
      if (!key || !expectedItems[key]) return;
      if (sourceCurrency !== currentCurrency) {
        sources[key] = null;
        return;
      }
      var compareAtValue = element.getAttribute('data-merlons-cart-pricing-compare-at-price');
      var catalogPriceValue = element.getAttribute('data-merlons-cart-pricing-catalog-price');
      if (compareAtValue === null || compareAtValue === '' || catalogPriceValue === null || catalogPriceValue === '') {
        sources[key] = null;
        return;
      }
      var parsedCatalogPrice = Number(catalogPriceValue);
      if (!Number.isSafeInteger(parsedCatalogPrice) || parsedCatalogPrice < 0) {
        sources[key] = null;
        return;
      }
      var entry = {
        variantId: identifier(element.getAttribute('data-merlons-cart-pricing-variant-id')),
        compareAt: Math.max(0, amount(compareAtValue, 0)),
        catalogPrice: parsedCatalogPrice,
        strict: element.getAttribute('data-merlons-cart-pricing-snapshot-trust') === 'strict'
          && tokensMatch(currentToken, element.getAttribute('data-merlons-cart-pricing-token'))
      };
      if (entry.variantId !== variantId(expectedItems[key])) {
        sources[key] = null;
        return;
      }
      if (sources[key] && (sources[key].variantId !== entry.variantId
        || sources[key].compareAt !== entry.compareAt
        || sources[key].catalogPrice !== entry.catalogPrice)) {
        sources[key] = null;
      } else if (sources[key]) {
        sources[key].strict = sources[key].strict && entry.strict;
      } else if (!Object.prototype.hasOwnProperty.call(sources, key)) {
        sources[key] = entry;
      }
    });
    cart.items.forEach(function (item) {
      var key = identifier(item && item.key);
      var entry = sources[key];
      if (entry && entry.variantId === variantId(item)) {
        map[key] = entry.compareAt;
        catalogByKey[key] = entry.catalogPrice;
      }
    });
    var completeCoverage = cart.items.every(function (item) {
      var entry = sources[identifier(item && item.key)];
      return entry && entry.variantId === variantId(item);
    });
    if (!completeCoverage) return fallbackComparisons(cart);
    var strictCoverage = cart.items.every(function (item) {
      return sources[identifier(item && item.key)].strict;
    });
    if (strictCoverage) {
      markCatalogPrices(map, catalogByKey);
      cacheComparisons(cart, map, { merlons_cart_pricing: true, token: currentToken, currency: currentCurrency, items: cart.items }, 'seed');
      return cachedComparisons(cart);
    }
    markCatalogPrices(map, catalogByKey);
    seededFallback = { signature: cartSignature(cart), map: copyComparisons(map), expiresAt: Date.now() + seededFallbackLifetime };
    return fallbackComparisons(cart);
  }

  function totals(cart, compareAtByKey) {
    var retail = 0;
    (cart && cart.items || []).forEach(function (item) {
      retail += line(item, compareAtByKey && compareAtByKey[item.key]).retail;
    });
    var final = amount(cart && cart.total_price, 0);
    var savings = Math.max(0, retail - final);
    return { retail: retail, final: final, savings: savings, percent: retail > final ? Math.round((savings * 100) / retail) : 0 };
  }

  function snapshotUrl(url) {
    snapshotRequestId += 1;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + '_merlons_pricing=' + snapshotRequestId;
  }

  function retryDelay() {
    return new Promise(function (resolve) {
      if (typeof window.setTimeout === 'function') window.setTimeout(resolve, 40);
      else resolve();
    });
  }

  function isSameOriginUrl(url) {
    if (!url) return false;
    var location = window.location;
    if (location && location.origin) {
      if (typeof URL !== 'function') {
        var rawUrl = String(url);
        if (!/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(rawUrl)) return true;
        var origin = String(location.origin).replace(/\/$/, '');
        return rawUrl === origin || rawUrl.indexOf(origin + '/') === 0 || rawUrl.indexOf(origin + '?') === 0 || rawUrl.indexOf(origin + '#') === 0;
      }
      try {
        return new URL(url, location.href || location.origin).origin === location.origin;
      } catch (error) {
        return false;
      }
    }
    return !/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(String(url));
  }

  function hasSameOriginResponse(requestUrl, response) {
    if (!isSameOriginUrl(requestUrl)) return false;
    // Test fixtures and older fetch shims may omit response.url. The requested
    // URL has already been checked in that case; browsers expose a final URL.
    return !response || !response.url || isSameOriginUrl(response.url);
  }

  function requestSnapshot(cart, url, attempt) {
    return window.fetch(snapshotUrl(url), {
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart pricing snapshot failed');
        return response.json().then(function (snapshot) {
          return { snapshot: snapshot, allowUnverifiedToken: hasSameOriginResponse(url, response) };
        });
      })
      .then(function (result) {
        var snapshot = result.snapshot;
        var relation = snapshotRelation(cart, snapshot, result.allowUnverifiedToken);
        if (relation.matches) {
          var map = comparisonMap(cart, snapshot);
          markComparisons(map, relation.cacheable ? 'strict' : 'live');
          if (relation.cacheable) {
            cacheComparisons(cart, map, snapshot);
            rememberFreshSnapshot(cart, map, snapshot);
          }
          seededFallback = null;
          return map;
        }
        if (!attempt) return retryDelay().then(function () { return requestSnapshot(cart, url, 1); });
        return fallbackComparisons(cart);
      })
      .catch(function () {
        if (!attempt) return retryDelay().then(function () { return requestSnapshot(cart, url, 1); });
        return fallbackComparisons(cart);
      });
  }

  function fetchSnapshot(cart, url) {
    if (!url || !window.fetch) return Promise.resolve(fallbackComparisons(cart));
    var signature = cartSignature(cart);
    if (freshSnapshot && freshSnapshot.signature === signature && Date.now() <= freshSnapshot.expiresAt) {
      return Promise.resolve(copyComparisons(freshSnapshot.map));
    }
    if (signature && snapshotRequests[signature]) return snapshotRequests[signature].then(copyComparisons);
    var request = requestSnapshot(cart, url, 0);
    if (!signature) return request;
    snapshotRequests[signature] = request;
    request.then(function () {
      if (snapshotRequests[signature] === request) delete snapshotRequests[signature];
    }, function () {
      if (snapshotRequests[signature] === request) delete snapshotRequests[signature];
    });
    return request;
  }

  function createRevisionGuard() {
    var revision = 0;
    return {
      next: function () { revision += 1; return revision; },
      isCurrent: function (candidate) { return candidate === revision; }
    };
  }

  function setMoney(element, value, format) {
    if (element) element.innerHTML = format(value);
  }

  function updateDom(root, cart, compareAtByKey, format) {
    if (!root || !cart || typeof format !== 'function') return;
    var itemsByKey = Object.create(null);
    (cart.items || []).forEach(function (item) { itemsByKey[item.key] = item; });
    Array.prototype.forEach.call(root.querySelectorAll('[data-merlons-cart-pricing-line]'), function (element) {
      var item = itemsByKey[element.getAttribute('data-merlons-cart-pricing-key')];
      if (!item) return;
      var hasComparison = compareAtByKey && Object.prototype.hasOwnProperty.call(compareAtByKey, item.key);
      var compareAtPrice = hasComparison
        ? Math.max(0, amount(compareAtByKey[item.key], 0))
        : Math.max(0, amount(element.getAttribute('data-merlons-cart-pricing-compare-at-price'), 0));
      var catalogPriceAttribute = element.getAttribute('data-merlons-cart-pricing-catalog-price');
      var currentCatalogPrice = catalogPrice(
        compareAtByKey,
        item.key,
        catalogPriceAttribute === null || catalogPriceAttribute === '' ? null : Number(catalogPriceAttribute)
      );
      var value = element.getAttribute('data-merlons-cart-pricing-unit') === 'true'
        ? unit(item, compareAtPrice)
        : line(item, compareAtPrice);
      if (hasComparison) {
        element.setAttribute('data-merlons-cart-pricing-compare-at-price', compareAtPrice);
      }
      if (currentCatalogPrice !== null) {
        element.setAttribute('data-merlons-cart-pricing-catalog-price', currentCatalogPrice);
      }
      element.setAttribute('data-merlons-cart-pricing-key', item.key);
      element.setAttribute('data-merlons-cart-pricing-variant-id', variantId(item));
      element.setAttribute('data-merlons-cart-pricing-quantity', currentQuantity(item));
      element.setAttribute('data-merlons-cart-pricing-token', cart.token || '');
      element.setAttribute('data-merlons-cart-pricing-currency', currencyCode(cart));
      element.setAttribute('data-merlons-cart-pricing-snapshot-trust', comparisonTrust(compareAtByKey) === 'strict' && hasComparison ? 'strict' : 'unverified');
      element.setAttribute('data-merlons-cart-retail', value.retail);
      element.setAttribute('data-merlons-cart-final', value.final);
      setMoney(element.querySelector('[data-merlons-cart-pricing-retail]'), value.retail, format);
      setMoney(element.querySelector('[data-merlons-cart-pricing-final]'), value.final, format);
      var showSavings = value.savings > 0;
      var retail = element.querySelector('[data-merlons-cart-pricing-retail]');
      var percent = element.querySelector('[data-merlons-cart-pricing-percent]');
      var liquidation = isLiquidation({ catalog_price: currentCatalogPrice }, compareAtPrice, liquidationOptions(element));
      element.setAttribute('data-merlons-cart-pricing-is-liquidation', liquidation ? 'true' : 'false');
      if (retail) retail.hidden = !showSavings;
      if (percent) {
        percent.hidden = !showSavings;
        percent.textContent = liquidation
          ? (element.getAttribute('data-merlons-cart-pricing-liquidation-label') || '')
          : (percent.getAttribute('data-merlons-cart-pricing-percent-format') || '−__MERLONS_PERCENT__%').replace('__MERLONS_PERCENT__', value.percent);
      }
    });
    var total = totals(cart, compareAtByKey);
    Array.prototype.forEach.call(root.querySelectorAll('[data-merlons-cart-pricing-summary]'), function (element) {
      element.setAttribute('data-merlons-cart-retail', total.retail);
      element.setAttribute('data-merlons-cart-final', total.final);
      var showSavings = total.savings > 0;
      var retailRow = element.querySelector('[data-merlons-cart-pricing-retail]');
      if (retailRow) retailRow.hidden = !showSavings;
      setMoney(element.querySelector('[data-merlons-cart-pricing-retail-value]'), total.retail, format);
      setMoney(element.querySelector('[data-merlons-cart-pricing-final-value]'), total.final, format);
      var percent = element.querySelector('[data-merlons-cart-pricing-percent]');
      if (percent) {
        percent.hidden = !showSavings;
        percent.textContent = (percent.getAttribute('data-merlons-cart-pricing-percent-format') || '−__MERLONS_PERCENT__%').replace('__MERLONS_PERCENT__', total.percent);
      }
    });
  }

  window.MerlonsCartPricing = { line: line, unit: unit, totals: totals, isLiquidation: isLiquidation, catalogPrice: catalogPrice, comparisons: comparisons, snapshotMatches: snapshotMatches, tokensMatch: tokensMatch, seedFromDom: seedFromDom, fetchSnapshot: fetchSnapshot, createRevisionGuard: createRevisionGuard, updateDom: updateDom };
})(window);
